import { ThreeElements } from '@react-three/fiber'
import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { AssetVizHeatmapDisplayState } from 'features/assetViz/defines'
import { RectSelection3D } from 'features/assetViz/objects/decor/RectSelection3D'
import { IconPrimitive3D } from 'features/assetViz/objects/primitives/IconPrimitive3D'
import { LabelPrimitive3D } from 'features/assetViz/objects/primitives/LabelPrimitive3D'
import { MarkupOutlines3D } from 'features/assetViz/objects/primitives/MarkupOutlines'
import React, { FC, ReactNode, useMemo } from 'react'
import { BufferGeometry } from 'three/src/core/BufferGeometry'
import {
  ComponentSurfaceWrappingMode,
  GeckoComponentModel,
  GeckoComponentRect,
  GeckoMarkupIcon,
  GeckoMarkupLabel,
  GeckoMarkupLayer,
} from 'types'

import { BaseLayerChunk3D } from './layers/BaseLayerChunk3D'
import { HeatmapLayerChunk3D } from './layers/HeatmapLayerChunk3D'
import { PhotogrammetryLayerChunk3D } from './layers/PhotogrammetryLayerChunk3D'
import { TextDataLayerChunk3D } from './layers/TextDataLayerChunk3D'
import { SurfaceProvider } from './util/SurfaceProvider'

export type Surface3DGroupProps = ThreeElements['group']

/**
 * The base 3D surface for rendering Gecko scan data on. Internally, this
 * component handles everything from markup layer to heatmap rendering.
 * @param bounds          The bounds of the chunk, in component coordinates
 * @param doWrapX         Whether this chunk wraps in the x direction
 * @param doWrapY         Whether this chunk wraps in the y direction
 * @param geometry        The 3D geometry (mesh) of this object
 * @param children        Any subcomponents of this surface
 * @param groupProps      Any positional/rotational information
 * @constructor
 */
export const Surface3D: FC<
  {
    // 2D dimensions
    bounds: GeckoComponentRect
    wrapMode?: ComponentSurfaceWrappingMode
    componentModel?: GeckoComponentModel
    // 3D Dimensions
    geometry: BufferGeometry
    // Component data
    children?: ReactNode
  } & Surface3DGroupProps
> = ({
  bounds,
  wrapMode = ComponentSurfaceWrappingMode.ComponentNoWrap,
  componentModel,
  geometry,
  children,
  ...groupProps
}) => {
  //----------------------------------------------------------------------------
  const {
    draftSelection,
    selection,
    componentDataFrames,
    markupLayers,
    showBinValues,
    activePhotogrammetryTexture,
    showPhotogrammetryTexture,
    heatmapDisplayState,
    markupLayerLabelVisibility,
  } = useAssetVizInternal()

  const cdf = useMemo(
    () =>
      componentDataFrames.find(
        (cdf) => cdf.tags.component === bounds.componentID
      ),
    [bounds, componentDataFrames]
  )

  //----------------------------------------------------------------------------

  const { iconsToDraw, labelsToDraw, componentMarkupLayers } = useMemo(() => {
    type PrimitivePropsType<T> = {
      key: string
      markupLayer: GeckoMarkupLayer
      primitive: T
    }
    const iconsToDraw = new Array<PrimitivePropsType<GeckoMarkupIcon>>()
    const labelsToDraw = new Array<PrimitivePropsType<GeckoMarkupLabel>>()

    // Parse hidden label layer IDs
    const hiddenLabelLayerIds = new Set(markupLayerLabelVisibility)

    const componentMarkupLayers = markupLayers.filter(
      (markupLayer) => markupLayer.tags.component === bounds.componentID
    )

    componentMarkupLayers.forEach((markupLayer) => {
      markupLayer.data.primitives.forEach((primitive) => {
        // The simplest possible optimization: eliminate primitives which
        // aren't within the bounds of this chunk.
        if (!GeckoComponentRect.Overlaps(primitive.bounds, bounds)) return

        const props = {
          // Use "#" as a delimiter, since it's an invalid Rolodex ID character
          key: markupLayer.id + '#' + primitive.id,
          markupLayer,
          primitive,
        }
        if (primitive instanceof GeckoMarkupIcon)
          iconsToDraw.push({ ...props, primitive })
        else if (primitive instanceof GeckoMarkupLabel) {
          // Only add labels if the layer's labels are not hidden
          if (!hiddenLabelLayerIds.has(markupLayer.id)) {
            labelsToDraw.push({ ...props, primitive })
          }
        }
      })
    })
    return {
      iconsToDraw,
      labelsToDraw,
      componentMarkupLayers,
    }
  }, [bounds, markupLayers, markupLayerLabelVisibility])

  //----------------------------------------------------------------------------

  return (
    <group {...groupProps}>
      <SurfaceProvider
        geometry={geometry}
        bounds={bounds}
        wrapMode={wrapMode}
        componentModel={componentModel}>
        <BaseLayerChunk3D />
        {!cdf ||
        heatmapDisplayState === AssetVizHeatmapDisplayState.Off ? null : (
          <HeatmapLayerChunk3D cdf={cdf} />
        )}
        {showPhotogrammetryTexture && !!activePhotogrammetryTexture && (
          <PhotogrammetryLayerChunk3D />
        )}
        {!showBinValues || !cdf ? null : <TextDataLayerChunk3D cdf={cdf} />}
        {!(selection instanceof GeckoComponentRect) ? null : (
          <RectSelection3D rect={selection} isDraft={false} />
        )}
        {!(draftSelection instanceof GeckoComponentRect) ? null : (
          <RectSelection3D rect={draftSelection} isDraft={true} />
        )}
        {componentMarkupLayers.map((markupLayer) => (
          <MarkupOutlines3D key={markupLayer.id} markupLayer={markupLayer} />
        ))}
        {iconsToDraw.map(({ key, ...props }) => (
          <IconPrimitive3D key={key} {...props} />
        ))}
        {labelsToDraw.map(({ key, ...props }) => (
          <LabelPrimitive3D key={key} {...props} />
        ))}
        {children}
      </SurfaceProvider>
    </group>
  )
}
